import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";

export interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  photographer_url: string;
  photographer_id: number;
  avg_color: string;
  src: {
    original: string;
    large2x: string;
    large: string;
    medium: string;
    small: string;
    portrait: string;
    landscape: string;
    tiny: string;
  };
  liked: boolean;
  alt: string;
}

export interface PexelsSearchResponse {
  page: number;
  per_page: number;
  photos: PexelsPhoto[];
  total_results: number;
  next_page?: string;
  prev_page?: string;
}

@Injectable({
  providedIn: "root",
})
export class PexelsImageService {
  private readonly apiUrl = "https://api.pexels.com/v1";
  private readonly apiKey = environment.pexelsApiKey;

  constructor(protected http: HttpClient) { }

  /**
   * Search for photos on Pexels
   * @param query Search terms
   * @param page Page number to retrieve (default: 1)
   * @param perPage Number of items per page (default: 15, max: 80)
   * @param orientation Filter by photo orientation (landscape, portrait, square)
   * @param size Filter by photo size (large, medium, small)
   * @param color Filter results by color (red, orange, yellow, green, turquoise, blue, violet, pink, brown, black, gray, white)
   * @param locale The locale of the search (en-US, pt-BR, es-ES, ca-ES, de-DE, it-IT, fr-FR, sv-SE, id-ID, pl-PL, ja-JP, zh-TW, zh-CN, ko-KR, th-TH, nl-NL, hu-HU, vi-VN, cs-CZ, da-DK, fi-FI, uk-UA, el-GR, ro-RO, nb-NO, sk-SK, tr-TR, ru-RU)
   */
  searchPhotos(
    query: string,
    page: number = 1,
    perPage: number = 15,
    orientation?: "landscape" | "portrait" | "square",
    size?: "large" | "medium" | "small",
    color?: "red" | "orange" | "yellow" | "green" | "turquoise" | "blue" | "violet" | "pink" | "brown" | "black" | "gray" | "white",
    locale?: string
  ): Observable<PexelsSearchResponse> {
    let params = new HttpParams()
      .set("query", query)
      .set("page", page.toString())
      .set("per_page", perPage.toString());

    if (orientation) {
      params = params.set("orientation", orientation);
    }

    if (size) {
      params = params.set("size", size);
    }

    if (color) {
      params = params.set("color", color);
    }

    if (locale) {
      params = params.set("locale", locale);
    }

    const headers = new HttpHeaders({
      Authorization: this.apiKey,
    });
    return this.http.get<PexelsSearchResponse>(`${this.apiUrl}/search`, {
      params,
      headers,
    });
  }

  /**
   * Get a single photo by ID
   * @param id The photo's ID
   */
  getPhoto(id: number): Observable<PexelsPhoto> {
    const headers = new HttpHeaders({
      Authorization: this.apiKey,
    });

    return this.http.get<PexelsPhoto>(`${this.apiUrl}/photos/${id}`, { headers });
  }
}
